import React from "react";
import { InlineMath } from "react-katex";
import {
  DigitRow,
  ORANGE,
  StepStrip,
  WrittenBoard,
  createNumericInputTask,
  digitsOf,
  padDigits,
  randInt,
} from "../sharedWrittenNatural";

function pow10(exp) {
  return 10 ** exp;
}

function randNDigit(digits) {
  return randInt(pow10(digits - 1), pow10(digits) - 1);
}

function randomNoTrailingZero(min, max) {
  let value = randInt(min, max);
  while (value % 10 === 0) value = randInt(min, max);
  return value;
}

function randomWithoutZeroDigit(digits) {
  let text = "";
  for (let i = 0; i < digits; i += 1) {
    text += String(randInt(i === 0 ? 1 : 2, 9));
  }
  return Number(text);
}

function pickOneDigitData() {
  const a = randomNoTrailingZero(123, 9876);
  const b = randInt(2, 9);
  return { a, b, result: a * b };
}

function pickTwoByTwoData() {
  const a = randomWithoutZeroDigit(2);
  const b = randomWithoutZeroDigit(2);
  return { a, b, result: a * b };
}

function pickFourByTwoData() {
  const a = randomNoTrailingZero(123, 9876);
  const b = randomWithoutZeroDigit(2);
  return { a, b, result: a * b };
}

function pickThreeByThreeData() {
  const a = randomWithoutZeroDigit(3);
  const b = randomWithoutZeroDigit(3);
  return { a, b, result: a * b };
}

function pickLargeByThreeData() {
  const digits = randInt(4, 5);
  const a = randomNoTrailingZero(pow10(digits - 1), pow10(digits) - 1);
  const b = randomWithoutZeroDigit(3);
  return { a, b, result: a * b };
}

function pickZerosData() {
  const baseA = randomNoTrailingZero(123, 987);
  const baseB = randomWithoutZeroDigit(randInt(2, 3));
  const zerosA = randInt(1, 2);
  const zerosB = randInt(1, 3);
  const a = baseA * 10 ** zerosA;
  const b = baseB * 10 ** zerosB;
  return { a, b, baseA, baseB, zerosA, zerosB, result: a * b };
}

function pickOneSideZerosData() {
  const baseA = randomNoTrailingZero(123, 987);
  const baseB = randomWithoutZeroDigit(randInt(2, 3));
  const zeros = randInt(1, 3);
  const zerosOnFirst = Math.random() < 0.5;
  const zerosA = zerosOnFirst ? zeros : 0;
  const zerosB = zerosOnFirst ? 0 : zeros;
  const a = baseA * 10 ** zerosA;
  const b = baseB * 10 ** zerosB;
  return { a, b, baseA, baseB, zerosA, zerosB, result: a * b };
}

function countTrailingZeros(value) {
  let text = String(value);
  let count = 0;
  while (text.endsWith("0")) {
    count += 1;
    text = text.slice(0, -1);
  }
  return count;
}

function partialSumMath(partials, result) {
  return `${partials.map((p) => p.value).join("+")}=${result}`;
}

export function multiplySingleRow(a, b) {
  const raw = a * b;
  const cols = Math.max(String(a).length + 1, String(raw).length);
  const top = padDigits(a, cols);
  const bottom = padDigits(b, cols);
  const result = Array(cols).fill(" ");
  const carries = Array(cols).fill(" ");
  const detailLines = [];
  const steps = [];
  let carry = 0;

  const topDigits = digitsOf(a);
  for (let source = topDigits.length - 1; source >= 0; source -= 1) {
    const digit = Number(topDigits[source]);
    const gridIdx = cols - (topDigits.length - source);
    const value = digit * b + carry;
    const written = value % 10;
    const carryOut = Math.floor(value / 10);
    const isLast = source === 0;
    const writtenValue = isLast && carryOut > 0 ? String(value) : String(written);

    if (isLast && carryOut > 0) {
      String(value)
        .split("")
        .forEach((d, idx, arr) => {
          result[gridIdx - arr.length + 1 + idx] = d;
        });
    } else {
      result[gridIdx] = String(written);
    }
    if (carryOut > 0 && !isLast) carries[gridIdx - 1] = String(carryOut);

    detailLines.push({ digit, b, carry, value, written, carryOut, isLast, writtenValue });
    steps.push({
      top,
      bottom,
      carries: [...carries],
      result: [...result],
      highlightTop: [gridIdx],
      highlightBottom: bottom.map((d, i) => (d !== " " ? i : -1)).filter((i) => i >= 0),
      highlightResult:
        isLast && carryOut > 0 ? Array.from({ length: String(value).length }, (_, i) => gridIdx - String(value).length + 1 + i) : [gridIdx],
      highlightCarry: carryOut > 0 && !isLast ? [gridIdx - 1] : [],
    });
    carry = carryOut;
  }

  steps.push({
    top,
    bottom,
    carries: [...carries],
    result: padDigits(raw, cols),
    highlightTop: [],
    highlightBottom: [],
    highlightResult: [],
    highlightCarry: [],
  });

  return { cols, top, bottom, carries, result: padDigits(raw, cols), detailLines, steps };
}

export function partialProducts(a, b) {
  const bDigits = digitsOf(b).map(Number);
  const partials = [];
  for (let i = bDigits.length - 1; i >= 0; i -= 1) {
    const digit = bDigits[i];
    const placeZeros = bDigits.length - 1 - i;
    partials.push({
      digit,
      placeZeros,
      value: a * digit * 10 ** placeZeros,
      rawValue: a * digit,
    });
  }
  return partials;
}

function partialCarryDigits(a, digit, cols) {
  const written = multiplySingleRow(a, digit);
  const padding = Array(Math.max(cols - written.carries.length, 0)).fill(" ");
  return [...padding, ...written.carries].slice(-cols);
}

function placeBeforeBoundary(value, cols, boundary, trailingZeros = 0) {
  const placed = Array(cols).fill(" ");
  const core = String(value).split("");
  const start = Math.max(boundary - core.length, 0);
  core.forEach((digit, idx) => {
    placed[start + idx] = digit;
  });
  for (let i = 0; i < trailingZeros; i += 1) {
    if (boundary + i < cols) placed[boundary + i] = "0";
  }
  return placed;
}

function dashedBoundaryStyle(boundary) {
  const digitTrack = "1.08ch";
  const columnGap = "0.18em";
  return `calc(1ch + ${boundary} * (${digitTrack} + ${columnGap}) + ${columnGap} * 0.35)`;
}

export function buildPartialSteps(data, zeroInfo = null) {
  const partials = partialProducts(data.a, data.b);
  const rows = [data.a, data.b, ...partials.map((p) => p.value), data.result];
  const cols = zeroInfo?.cols ?? Math.max(...rows.map((x) => String(x).length));
  const steps = [];

  partials.forEach((_, idx) => {
    steps.push({
      data,
      partials,
      cols,
      visiblePartials: idx + 1,
      showResult: false,
      highlightPartial: idx,
      highlightResult: false,
      zeroInfo,
    });
  });

  steps.push({
    data,
    partials,
    cols,
    visiblePartials: partials.length,
    showResult: true,
    highlightPartial: null,
    highlightResult: true,
    zeroInfo,
  });
  steps.push({
    data,
    partials,
    cols,
    visiblePartials: partials.length,
    showResult: true,
    highlightPartial: null,
    highlightResult: false,
    zeroInfo,
  });

  return { partials, steps };
}

function BoardSingle({ step }) {
  return (
    <WrittenBoard>
      <DigitRow digits={step.carries} small highlightIndices={step.highlightCarry} />
      <DigitRow digits={step.top} highlightIndices={step.highlightTop} />
      <DigitRow sign="x" digits={step.bottom} highlightIndices={step.highlightBottom} />
      <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
      <DigitRow digits={step.result} highlightIndices={step.highlightResult} />
    </WrittenBoard>
  );
}

export function BoardPartials({ step }) {
  const {
    data,
    partials,
    cols,
    visiblePartials,
    showResult,
    highlightPartial,
    highlightResult,
    zeroInfo,
  } = step;
  const boundary = zeroInfo?.boundary;
  const visibleCarryRows = partials
    .slice(0, visiblePartials)
    .map((partial, idx) => ({
      idx,
      digits:
        zeroInfo && boundary != null
          ? placeBeforeBoundary(
              Number(partialCarryDigits(data.a, partial.digit, boundary).join("").trim() || 0),
              cols,
              boundary
            )
          : partialCarryDigits(data.a, partial.digit, cols),
    }))
    .filter((row) => row.digits.some((digit) => digit !== " "));
  const resultHighlight = highlightResult
    ? (zeroInfo && boundary != null
        ? placeBeforeBoundary(data.result, cols, boundary, zeroInfo.totalZeros)
        : padDigits(data.result, cols)
      )
        .map((d, idx) => (d !== " " ? idx : -1))
        .filter((idx) => idx >= 0)
    : [];
  const topDigits =
    zeroInfo && boundary != null
      ? placeBeforeBoundary(data.a, cols, boundary, zeroInfo.zerosA)
      : padDigits(data.a, cols);
  const bottomDigits =
    zeroInfo && boundary != null
      ? placeBeforeBoundary(data.b, cols, boundary, zeroInfo.zerosB)
      : padDigits(data.b, cols);
  const topMuted =
    zeroInfo && boundary != null
      ? Array.from({ length: zeroInfo.zerosA }, (_, idx) => boundary + idx)
      : [];
  const bottomMuted =
    zeroInfo && boundary != null
      ? Array.from({ length: zeroInfo.zerosB }, (_, idx) => boundary + idx)
      : [];
  const finalResultDigits =
    zeroInfo && boundary != null
      ? placeBeforeBoundary(data.result, cols, boundary, zeroInfo.totalZeros)
      : padDigits(data.result, cols);
  const finalResultMuted =
    zeroInfo && boundary != null
      ? Array.from({ length: zeroInfo.totalZeros }, (_, idx) => boundary + idx)
      : [];

  return (
    <WrittenBoard>
      <div style={{ position: "relative", display: "inline-block" }}>
        {zeroInfo && boundary != null ? (
          <div
            style={{
              position: "absolute",
              top: "0.1em",
              bottom: "0.1em",
              left: dashedBoundaryStyle(boundary),
              borderLeft: `1px dashed ${ORANGE}`,
              pointerEvents: "none",
            }}
          />
        ) : null}
        {visibleCarryRows.map((row) => {
          const carryHighlight =
            highlightPartial === row.idx
              ? row.digits
                  .map((digit, colIdx) => (digit !== " " ? colIdx : -1))
                  .filter((colIdx) => colIdx >= 0)
              : [];
          return (
            <DigitRow
              key={`carry-${row.idx}`}
              digits={row.digits}
              small
              highlightIndices={carryHighlight}
            />
          );
        })}
        <DigitRow digits={topDigits} mutedIndices={topMuted} />
        <DigitRow sign="x" digits={bottomDigits} mutedIndices={bottomMuted} />
        <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
        {partials.slice(0, visiblePartials).map((p, idx) => {
          const row =
            zeroInfo && boundary != null
              ? placeBeforeBoundary(p.value, cols, boundary)
              : padDigits(p.value, cols);
          const mutedZeros =
            p.placeZeros > 0
              ? row
                  .map((_, colIdx) =>
                    colIdx >= (zeroInfo && boundary != null ? boundary : row.length) - p.placeZeros &&
                    colIdx < (zeroInfo && boundary != null ? boundary : row.length)
                      ? colIdx
                      : -1
                  )
                  .filter((colIdx) => colIdx >= 0)
              : [];
          const rowHighlight =
            highlightPartial === idx
              ? row
                  .map((d, colIdx) => (d !== " " && !mutedZeros.includes(colIdx) ? colIdx : -1))
                  .filter((colIdx) => colIdx >= 0)
              : [];
          return (
            <DigitRow
              key={idx}
              sign={idx === 0 ? "" : "+"}
              digits={row}
              highlightIndices={rowHighlight}
              mutedIndices={mutedZeros}
            />
          );
        })}
        {showResult ? (
          <>
            <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
            <DigitRow
              digits={finalResultDigits}
              highlightIndices={resultHighlight}
              mutedIndices={finalResultMuted}
            />
          </>
        ) : null}
      </div>
    </WrittenBoard>
  );
}

function ZeroSeparatedBoard({ data }) {
  const totalZeros = countTrailingZeros(data.result);
  const values = [data.a, data.b, data.result];
  const cols = Math.max(...values.map((value) => String(value).length));
  const boundary = totalZeros > 0 ? cols - totalZeros : null;
  const digitTrack = "1.08ch";
  const columnGap = "0.18em";
  const boundaryLeft =
    boundary == null
      ? null
      : `calc(1ch + ${boundary} * (${digitTrack} + ${columnGap}) + ${columnGap} * 0.35)`;
  function mutedTrailingZeros(value) {
    const zeros = countTrailingZeros(value);
    return zeros > 0 ? Array.from({ length: zeros }, (_, idx) => cols - zeros + idx) : [];
  }

  return (
    <WrittenBoard>
      <div style={{ position: "relative", display: "inline-block" }}>
        {boundaryLeft ? (
          <div
            style={{
              position: "absolute",
              top: "0.1em",
              bottom: "0.1em",
              left: boundaryLeft,
              borderLeft: `1px dashed ${ORANGE}`,
              pointerEvents: "none",
            }}
          />
        ) : null}
        <DigitRow digits={padDigits(data.a, cols)} mutedIndices={mutedTrailingZeros(data.a)} />
        <DigitRow sign="x" digits={padDigits(data.b, cols)} mutedIndices={mutedTrailingZeros(data.b)} />
        <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)" }} />
        <DigitRow digits={padDigits(data.result, cols)} mutedIndices={mutedTrailingZeros(data.result)} />
      </div>
    </WrittenBoard>
  );
}

function MultiplicationDetails({ lines }) {
  return (
    <ul>
      {lines.map((line, idx) => (
        <li key={idx}>
          <InlineMath
            math={
              line.carry
                ? `${line.digit}\\cdot ${line.b}+${line.carry}=${line.value}`
                : `${line.digit}\\cdot ${line.b}=${line.value}`
            }
          />{" "}
          {line.isLast ? (
            <>
              {"\u2192"} zapisujemy <InlineMath math={`${line.writtenValue}`} />
            </>
          ) : line.carryOut > 0 ? (
            <>
              {"\u2192"} zapisujemy <InlineMath math={`${line.written}`} />, przenosimy{" "}
              <InlineMath math={`${line.carryOut}`} />
            </>
          ) : (
            <>
              {"\u2192"} zapisujemy <InlineMath math={`${line.written}`} />
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

export function PartialDetails({ data, partials }) {
  return (
    <ul>
      {partials.map((p, idx) => (
        <li key={idx}>
          <InlineMath math={`${data.a}\\cdot ${p.digit}=${p.rawValue}`} />
          {p.placeZeros > 0 ? (
            <>
              , mnożymy przez <InlineMath math={`${10 ** p.placeZeros}`} /> i zapisujemy{" "}
              <InlineMath math={`${p.value}`} />
            </>
          ) : (
            <>
              {" "}
              i zapisujemy <InlineMath math={`${p.value}`} />
            </>
          )}
        </li>
      ))}
      {partials.length > 1 ? (
        <li>
          Dodajemy iloczyny częściowe pisemnie:{" "}
          <InlineMath math={partialSumMath(partials, data.result)} />.
        </li>
      ) : null}
    </ul>
  );
}

export function explainOneDigit(data) {
  const written = multiplySingleRow(data.a, data.b);
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Ustawiamy liczby pionowo i mnożymy od prawej
        strony. Przeniesienia dopisujemy nad kolejną kolumną.
      </p>
      <MultiplicationDetails lines={written.detailLines} />
      <StepStrip steps={written.steps.map((step, idx) => <BoardSingle key={idx} step={step} />)} />
      <p>
        <strong>Wynik:</strong> <InlineMath math={`${data.result}`} />
      </p>
    </div>
  );
}

export function explainPartials(data) {
  const { partials, steps } = buildPartialSteps(data);
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Mnożymy pierwszą liczbę przez kolejne cyfry
        drugiej liczby, zaczynając od prawej strony.
      </p>
      <PartialDetails data={data} partials={partials} />
      <p>
        <strong>Krok 2:</strong> Dodajemy iloczyny częściowe sposobem pisemnym.
      </p>
      <StepStrip
        alignItems="flex-start"
        steps={steps.map((step, idx) => <BoardPartials key={idx} step={step} />)}
      />
      <p>
        <strong>Wynik:</strong> <InlineMath math={`${data.result}`} />
      </p>
    </div>
  );
}

export function explainZeros(data) {
  const compact = {
    a: data.baseA,
    b: data.baseB,
    result: data.baseA * data.baseB,
  };
  const zeros = data.zerosA + data.zerosB;
  const zeroInfo = {
    zerosA: data.zerosA,
    zerosB: data.zerosB,
    totalZeros: zeros,
    cols: String(data.result).length,
    boundary: String(data.result).length - zeros,
  };
  const { partials, steps } = buildPartialSteps(compact, zeroInfo);
  const showWrittenZeroResult = partials.length > 1;
  const visibleSteps = showWrittenZeroResult
    ? steps
    : steps.filter((step) => !step.showResult);
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Oddzielamy końcowe zera. Najpierw mnożymy{" "}
        <InlineMath math={`${data.baseA}\\cdot ${data.baseB}`} />.
      </p>
      <PartialDetails data={compact} partials={partials} />
      <StepStrip
        alignItems="flex-start"
        steps={visibleSteps.map((step, idx) => <BoardPartials key={idx} step={step} />)}
      />
      <p>
        <strong>Krok 2:</strong> Łącznie było {zeros} {zeros === 1 ? "zero" : "zera"},
        więc dopisujemy je na końcu:{" "}
        <InlineMath math={`${compact.result}\\cdot ${10 ** zeros}=${data.result}`} />.
      </p>
    </div>
  );
}

function makeTask(type, pickData, explain) {
  return createNumericInputTask({
    type,
    pickData,
    renderTaskText: (data) => (
      <>
        Oblicz sposobem pisemnym: <InlineMath math={`${data.a}\\cdot ${data.b}`} />.
      </>
    ),
    explain,
  });
}

export const NaturalWrittenMultiplyOneDigitTask = makeTask(
  "NaturalWrittenMultiplyOneDigitTask",
  pickOneDigitData,
  explainOneDigit
);
export const NaturalWrittenMultiplyTwoByTwoTask = makeTask(
  "NaturalWrittenMultiplyTwoByTwoTask",
  pickTwoByTwoData,
  explainPartials
);
export const NaturalWrittenMultiplyFourByTwoTask = makeTask(
  "NaturalWrittenMultiplyFourByTwoTask",
  pickFourByTwoData,
  explainPartials
);
export const NaturalWrittenMultiplyThreeByThreeTask = makeTask(
  "NaturalWrittenMultiplyThreeByThreeTask",
  pickThreeByThreeData,
  explainPartials
);
export const NaturalWrittenMultiplyLargeByThreeTask = makeTask(
  "NaturalWrittenMultiplyLargeByThreeTask",
  pickLargeByThreeData,
  explainPartials
);
export const NaturalWrittenMultiplyOneSideZerosTask = makeTask(
  "NaturalWrittenMultiplyOneSideZerosTask",
  pickOneSideZerosData,
  explainZeros
);
export const NaturalWrittenMultiplyWithZerosTask = makeTask(
  "NaturalWrittenMultiplyWithZerosTask",
  pickZerosData,
  explainZeros
);
